import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam, type ExamState } from '../../exam.js';
import { StepState } from '../../extraction-progress.js';
import {
  evaluateGradingSchemeSectionComplete,
  evaluateLanguageUseSchemeSectionComplete,
} from '../../utils/solution-completeness.js';
import { hasReceivedAnySubmission } from '../../utils/exam-lock.js';
import { resolveIsStudentOwnedExam } from '../../utils/exam-role.js';
import { dispatchJobs } from '../jobs/dispatch.js';
import { GENERATION_JOB_TYPE } from '../jobs/job.js';
import { hasGradingSchemeContent, hasMasterSolutionContent } from '../agents/tree-generation-utils.js';

export const gradingSchemeToolDef = [
  {
    type: 'function',
    function: {
      name: 'generate_grading_scheme',
      description:
        'Generiert KI-basierte Bewertungsschlüssel/Grading Scheme für die Aufgaben einer Klausur und ERGÄNZT dabei fehlende Teile: Aufgaben, die schon ein Bewertungsschema haben (auch aus einem Upload), bleiben unverändert — es werden nur die Aufgaben ohne Bewertungsschema erzeugt. Nutze dieses Tool also auch, wenn ein hochgeladenes Bewertungsschema unvollständig ist und der Nutzer die fehlenden Teile ergänzen möchte. Voraussetzung ist eine Musterlösung (hochgeladen oder generiert) — sie darf auch gerade erst erzeugt werden.',
      parameters: {
        type: 'object',
        properties: {
          examId: {
            type: 'string',
            description: 'Die ID der aktuellen Klausur',
          },
        },
        required: ['examId'],
      },
    },
  },
];

const examHasMasterSolution = (exam: any): boolean => {
  if (Array.isArray(exam?.masterSolutionIds) && exam.masterSolutionIds.length > 0) {
    return true;
  }

  const taskHasMasterSolution = (task: any): boolean => {
    const ms = task?.masterSolution;
    const hasCurrentTaskSolution =
      typeof ms === 'string' || typeof ms?.content === 'string' || typeof ms?.doc?.content === 'string';

    if (hasCurrentTaskSolution) {
      return true;
    }

    if (Array.isArray(task?.subTasks)) {
      return task.subTasks.some((subTask: any) => taskHasMasterSolution(subTask));
    }

    return false;
  };

  return Array.isArray(exam?.tasks) && exam.tasks.some((task: any) => taskHasMasterSolution(task));
};

const collectGenerationTaskStats = (exam: any) => {
  let totalAnswerable = 0;
  let withMasterSolution = 0;
  let withGradingScheme = 0;

  const walk = (task: any) => {
    if (!task) return;

    const needsAnswer = task?.needsAnswer !== false;
    if (needsAnswer) {
      totalAnswerable += 1;

      // Same definition the generation agents use to decide what to target (see
      // tree-generation-utils.hasMasterSolutionContent): a bounding box counts, so an uploaded
      // image-only region is not reported as a gap the generation is about to fill.
      if (hasMasterSolutionContent(task)) withMasterSolution += 1;
      if (hasGradingSchemeContent(task)) withGradingScheme += 1;
    }

    if (Array.isArray(task?.subTasks)) {
      task.subTasks.forEach(walk);
    }
  };

  if (Array.isArray(exam?.tasks)) {
    exam.tasks.forEach(walk);
  }

  return { totalAnswerable, withMasterSolution, withGradingScheme };
};

const buildGradingSchemeToolStatusMessage = (exam: any): string => {
  const stats = collectGenerationTaskStats(exam);
  const gsState = exam?.examState?.gradingSchemeProgress;
  const gsStatus = gsState?.status;
  const taskExtractionStatus = exam?.examState?.taskExtractionProgress?.status;

  const upstreamStatus = exam?.examState?.masterSolutionProgress?.status;

  const eligibleForGS = stats.withMasterSolution;

  if (gsStatus === 'ERROR') {
    return 'SYSTEM INFO: Bei der Erstellung des Bewertungsschemas ist ein Fehler aufgetreten. Bitte versuche es erneut.';
  }

  if (
    gsStatus === 'COMPLETED' ||
    (stats.totalAnswerable > 0 &&
      taskExtractionStatus === 'COMPLETED' &&
      upstreamStatus === 'COMPLETED' &&
      stats.withGradingScheme >= stats.totalAnswerable)
  ) {
    return `SYSTEM INFO: Das Bewertungsschema ist fertig (${stats.withGradingScheme}/${stats.totalAnswerable} Aufgaben) und in der Datenbank verfügbar. Schlage dem User vor mit der Bewertung zu starten oder das Bewertungsschema im Canvas zu überprüfen.`;
  }

  if (taskExtractionStatus !== 'COMPLETED') {
    return 'SYSTEM INFO: Die Erstellung des Bewertungsschemas wurde gestartet und wartet aktuell auf den Abschluss der Aufgabenextraktion. Teile dem User mit, dass ihm der Fortschritt der Extraktion und der Bewertungsschemaserstellung in der Statusleiste am rechten Bildschirmrand angezeigt wird und schlage dem User vor mit der Bewertung zu starten.';
  }

  if (upstreamStatus !== 'COMPLETED') {
    return 'SYSTEM INFO: Die Erstellung des Bewertungsschemas wurde gestartet und wartet aktuell auf verfügbare Musterlösungen. Teile dem User mit, dass ihm der Fortschritt der Musterlösungserstellung und der Bewertungsschemaserstellung in der Statusleiste am rechten Bildschirmrand angezeigt wird und schlage dem User vor mit der Bewertung zu starten.';
  }

  if (eligibleForGS === 0) {
    return 'SYSTEM INFO: Die Erstellung des Bewertungsschemas wurde gestartet. Aktuell liegen noch keine Aufgaben mit Musterlösung vor. Teile dem User mit, dass ihm der Fortschritt der Bewertungsschemaserstellung in der Statusleiste am rechten Bildschirmrand angezeigt wird und schlage dem User vor mit der Bewertung zu starten.';
  }

  // Same as the master solution: some tasks already carry a scheme (typically an incomplete upload) and
  // this run only fills the rest. Must be checked before the IN_PROGRESS branch, which is always true here
  // because the caller just started the run.
  if (stats.withGradingScheme > 0 && stats.withGradingScheme < stats.totalAnswerable) {
    const missing = stats.totalAnswerable - stats.withGradingScheme;
    return `SYSTEM INFO: Die fehlenden Teile des Bewertungsschemas werden jetzt im Hintergrund ergänzt (${missing} von ${stats.totalAnswerable} Aufgaben ohne Bewertungsschema). Die bereits vorhandenen Teile bleiben unverändert — sage dem User ausdrücklich, dass nur die fehlenden Teile ergänzt werden und nichts überschrieben wird. Der Fortschritt ist in der Statusleiste am rechten Bildschirmrand zu sehen.`;
  }

  if (gsStatus === 'IN_PROGRESS' || (gsState?.startedForTasks || []).length > 0) {
    return `SYSTEM INFO: Die Erstellung des Bewertungsschemas läuft (${stats.withGradingScheme}/${eligibleForGS} Aufgaben mit Musterlösung fertig). Teile dem User mit, dass ihm der Fortschritt der Bewertungsschemaserstellung in der Statusleiste am rechten Bildschirmrand angezeigt wird ist und schlage dem User vor mit der Bewertung zu starten.`;
  }

  return 'SYSTEM INFO: Die Erstellung des Bewertungsschemas wurde gestartet und läuft im Hintergrund. Teile dem User mit, dass ihm der Fortschritt der Bewertungsschemaserstellung in der Statusleiste am rechten Bildschirmrand angezeigt wird und schlage dem User vor mit der Bewertung zu starten.';
};

export async function handleGenerateGradingScheme(exam: Exam, toolCallArgs: any): Promise<object> {
  const examIdToUse = toolCallArgs.examId || exam?._id?.toString();
  let toolResultContent = '';
  if (!examIdToUse) {
    toolResultContent = 'Fehler: Keine Klausur-ID für die Generierung des Erwartungshorizonts gefunden.';
  } else {
    const examCollection = await getCollection(Exam);
    const currentExam = await examCollection.findOne({ _id: examIdToUse });

    if (!currentExam) {
      toolResultContent = 'Fehler: Die angegebene Klausur konnte nicht gefunden werden.';
    } else if ((await resolveIsStudentOwnedExam(examIdToUse)) && !hasReceivedAnySubmission(currentExam)) {
      // Same block as master-solution.tool.ts's own — a grading scheme is effectively the answer key's
      // point values, which the student must not be able to see before handing in their own attempt.
      toolResultContent =
        'SYSTEM INFO: Abbruch. Dies ist die eigene Übungsklausur des Nutzers (Schüler-Account) und wurde noch nicht abgegeben — das Bewertungsschema bleibt bis dahin gesperrt. Weise den Nutzer darauf hin, dass er die Klausur zuerst online ausfüllen und abgeben muss (oder seine bearbeitete Klausur hochladen), bevor das Bewertungsschema erstellt werden kann.';
    } else {
      const hasMasterSolution = examHasMasterSolution(currentExam);
      // Whichever producer is behind it — extraction or generation — the master solution has one state.
      const msStatus = currentExam.examState?.masterSolutionProgress?.status;
      const msProducer = currentExam.examState?.masterSolutionProgress?.producer;
      const msSourceInProgressOrDone = msStatus === 'IN_PROGRESS' || msStatus === 'COMPLETED';
      logger.info(
        { msStatus, msProducer, msSourceInProgressOrDone },
        'Status der Musterlösung als Voraussetzung für die Erwartungshorizonterstellung',
      );
      // The master solution stays a real precondition — the scheme is derived from it — but it may still be
      // running: generating both in one tool-use phase is an explicitly supported flow.
      //
      // There is no "a grading scheme already exists" guard. `runGradingSchemeAgent` fills only the
      // answerable tasks that have none, so an incomplete upload gets completed and the existing parts stay
      // as they are. Whether anything is left to fill is decided by `gradingSchemeAvailability`.
      if (!hasMasterSolution && !msSourceInProgressOrDone) {
        toolResultContent =
          'SYSTEM INFO: Abbruch. Es gibt noch keine Musterlösung. Bitte den Nutzer auffordern, zuerst eine Musterlösung hochzuladen oder zu generieren.';
      } else {
        // See `master-solution.tool.ts`. Each root's job additionally waits on its OWN subtree's master
        // solutions, so a scheme starts as soon as that subtree has one rather than after the entire
        // master-solution run — which is what makes generating both in one turn, the flow noted above,
        // actually interleave.
        await dispatchJobs(GENERATION_JOB_TYPE.gradingScheme, examIdToUse);

        // Proceed immediately to check status and respond to the user
        const refreshedExam = await examCollection.findOne({ _id: examIdToUse });
        toolResultContent = buildGradingSchemeToolStatusMessage(refreshedExam);
        logger.info({ toolResultContent }, 'Aktueller Status der Bewertungsschemaserstellung nach Tool-Call');
      }
    }
  }
  return { content: toolResultContent };
}

/**
 * Offered exactly while there is a task the generation could still fill — content OR language-use. See
 * `masterSolutionAvailability` for the reasoning; things that differ here:
 *  - The section check is used rather than the full completeness check for CONTENT. A task with criteria
 *    but no points counts as incomplete for GRADING, yet the generation cannot fix it (it does not
 *    overwrite existing criteria) — offering the tool for it would be a dead end. Points get completed in
 *    the canvas.
 *  - LANGUAGE-USE completeness (`evaluateLanguageUseSchemeSectionComplete`) is checked the same way: a
 *    grammar-eligible task missing its rubric keeps the tool offered, so a task manually retyped to a
 *    grammar-eligible type after its content was already generated still gets picked up.
 *  - The master solution precondition is deliberately NOT checked. The tool list is built once per turn, so
 *    gating on the master solution's state would break the documented chain of generating a master solution
 *    and a grading scheme within a single tool-use phase. That precondition lives in the handler.
 *  - Same student-self-practice check as `masterSolutionAvailability`, and for the same reason: a grading
 *    scheme is effectively the answer key's point values, which must stay hidden until the student submits.
 */
export function gradingSchemeAvailability(examState: ExamState, exam?: Exam, role?: 'teacher' | 'student'): boolean {
  if (!exam) return true;
  if (role === 'student' && !hasReceivedAnySubmission(exam)) return false;

  // See `masterSolutionAvailability`: the id array records the upload, and a state owned by a generation is
  // not an upload awaiting extraction.
  const gsProgress = examState?.gradingSchemeProgress;
  const uploadWaitingForExtraction =
    Array.isArray((exam as any)?.gradingSchemeIds) &&
    ((exam as any).gradingSchemeIds as unknown[]).length > 0 &&
    // Neither a generation NOR a refine owns a state that says anything about the upload's extraction.
    gsProgress?.producer !== 'generation' &&
    gsProgress?.producer !== 'refine' &&
    (gsProgress?.status === StepState.IN_PROGRESS ||
      gsProgress?.status === StepState.NOT_STARTED ||
      !gsProgress?.status);
  if (uploadWaitingForExtraction) return false;

  const contentComplete = evaluateGradingSchemeSectionComplete((exam as any).tasks).complete;
  const languageUseComplete = evaluateLanguageUseSchemeSectionComplete(
    (exam as any).tasks,
    (exam as any).languageUseSchemeRules,
  ).complete;
  return !(contentComplete && languageUseComplete);
}
